import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../Reveal";
import { Stamp } from "../Stamp";

const photos = [
  { src: "/students/elev-2.jpg", name: "Alături de instructor", tag: "Examen luat", pos: "50% 25%" },
  { src: "/students/elev-3.jpg", name: "Zi de examen reușită", tag: "Promovate", pos: "50% 20%" },
  { src: "/students/elev-1.jpg", name: "Direct de la centrul de examinare", tag: "Permis obținut", pos: "50% 45%" },
];

const Photo = ({ p, i, fig, onOpen, big = false }) => (
  <figure className={big ? "h-full flex flex-col" : ""}>
    <button
      type="button"
      onClick={onOpen}
      data-testid={`gallery-item-${i}`}
      className={`group relative block w-full rounded-2xl overflow-hidden ring-1 ring-slate-900/10 ${big ? "aspect-[4/5] lg:aspect-auto lg:flex-1" : "aspect-[4/3]"}`}
    >
      <img
        src={p.src}
        alt={`Elev promovat — ${p.name}`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        style={{ objectPosition: p.pos }}
      />
      <Stamp className="absolute top-4 right-4 scale-[0.82] origin-top-right" />
    </button>
    <figcaption className="mt-4 pt-3 border-t border-slate-200 flex items-baseline flex-wrap gap-x-3 gap-y-1">
      <span aria-hidden="true" className="font-display font-bold text-slate-300 text-sm">{fig}</span>
      <span className="text-[#1565C0] font-semibold text-sm">{p.tag}</span>
      <span className="text-sm font-semibold text-slate-900">{p.name}</span>
      <span className="text-sm text-slate-500">· Categoria B · Pașcani</span>
    </figcaption>
  </figure>
);

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i + photos.length - 1) % photos.length); }, []);
  const next = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % photos.length); }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close, prev, next]);

  return (
    <section id="galerie" data-testid="gallery-section" className="py-24 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1565C0]">Elevi reali · rezultate reale</p>
          <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 max-w-2xl">
            Momente adevărate din ziua examenului
          </h2>
          <p className="mt-4 text-slate-600 text-lg max-w-xl">
            Fără poze de stoc. Aceștia sunt elevii noștri, în fața mașinii Auto Valy Drive, cu foaia de examen în mână.
          </p>
        </Reveal>

        {/* Pagina de probe — colaj de revistă: o dovadă mare, două decalate */}
        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-12">
          <Reveal className="lg:col-span-8 h-full">
            <Photo p={photos[1]} i={1} fig="01" onOpen={() => setIndex(1)} big />
          </Reveal>
          <div className="lg:col-span-4 grid gap-10 content-start lg:pt-24">
            <Reveal delay={0.1}>
              <Photo p={photos[0]} i={0} fig="02" onOpen={() => setIndex(0)} />
            </Reveal>
            <Reveal delay={0.2}>
              <Photo p={photos[2]} i={2} fig="03" onOpen={() => setIndex(2)} />
            </Reveal>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            data-testid="lightbox"
            className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <button onClick={close} data-testid="lightbox-close" aria-label="Închide" className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
              <X size={22} />
            </button>

            <button onClick={prev} data-testid="lightbox-prev" aria-label="Anterior" className="absolute left-3 sm:left-6 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
              <ChevronLeft size={24} />
            </button>
            <button onClick={next} data-testid="lightbox-next" aria-label="Următor" className="absolute right-3 sm:right-6 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
              <ChevronRight size={24} />
            </button>

            <motion.figure
              key={index}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-lg w-full"
            >
              <img src={photos[index].src} alt={photos[index].name} className="w-full max-h-[78vh] object-contain rounded-2xl shadow-2xl" />
              <figcaption className="mt-4 text-center">
                <span className="inline-flex items-center gap-1.5 bg-[#1565C0] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  <CheckCircle2 size={13} /> {photos[index].tag}
                </span>
                <p className="mt-2 text-white font-display font-semibold">{photos[index].name}</p>
                <p className="text-white/60 text-sm">Categoria B · Pașcani</p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
